import React, { useEffect, useState } from "react";
import cover from "../res/peaci-ched-anniv.jpg";
import heroImg from "../res/peaciBldg.png";
import { db } from "../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";

const BackToTop = () => (
  <a
    href="#top"
    className="inline-flex items-center gap-1.5 mt-8 px-4 py-2 rounded-full text-sm font-medium text-amber-600 border border-amber-300 hover:bg-amber-50 hover:border-amber-500 transition-colors duration-200 self-end"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
    </svg>
    Back to Top
  </a>
);

const QuoteIcon = ({ className = "" }) => (
  <svg
    aria-hidden="true"
    className={`w-12 h-12 ${className}`}
    viewBox="0 0 24 27"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14.017 18L14.017 10.609C14.017 4.905 17.748 1.039 23 0L23.995 2.151C21.563 3.068 20 5.789 20 8H24V18H14.017ZM0 18V10.609C0 4.905 3.748 1.038 9 0L9.996 2.151C7.563 3.068 6 5.789 6 8H9.983L9.983 18L0 18Z"
      fill="currentColor"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2.5}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const SideNav = ({ activeSection }) => (
  <nav className="hidden lg:flex fixed left-6 top-1/2 -translate-y-1/2 z-30 flex-col gap-4">
    {navLinks.map(({ href, label }) => {
      const id = href.replace("#", "");
      const isActive = activeSection === id;
      return (
        <a href={href} key={href} className="group flex items-center gap-3">
          <span
            className={`w-3 h-3 rounded-full border-2 border-amber-500 transition-all duration-200 flex-shrink-0 ${
              isActive ? "bg-amber-500 scale-125" : "bg-transparent group-hover:bg-amber-500"
            }`}
          />
          <span
            className={`text-xs font-semibold bg-white shadow-md px-2.5 py-1 rounded-lg whitespace-nowrap transition-all duration-200 ${
              isActive
                ? "opacity-100 translate-x-0 text-amber-600"
                : "opacity-0 -translate-x-1 text-gray-700 group-hover:opacity-100 group-hover:translate-x-0"
            }`}
          >
            {label}
          </span>
        </a>
      );
    })}
  </nav>
);

const stats = [
  { value: "2007", label: "Year Founded" },
  { value: "450+", label: "First Enrollment" },
  { value: "18+", label: "Years of Service" },
];

const timelineMilestones = [
  {
    year: "2007",
    title: "Founding",
    body: "The Philippine Engineering and Agro-Industrial College, Inc. (PEACI) was founded by a group of Meranao engineers and duly registered with the Securities and Exchange Commission.",
  },
  {
    year: "2008",
    title: "First Students",
    body: "PEACI opened its doors in Summer 2008 with 450 students enrolled in the 1st PEACI Summer Academic Enhancement Program. In June 2008, 400 pupils officially enrolled for the academic year.",
  },
  {
    year: "2016",
    title: "Senior High School & New Programs",
    body: "In line with the K-12 Education Curriculum, PEACI opened Senior High School offering STEM, HUMSS, and TVL tracks. Additional degree programs were also introduced, including BSEd Mathematics.",
  },
  {
    year: "2021",
    title: "Program Expansion",
    body: "PEACI continues to expand its academic offerings with new degree programs including Bachelor of Science in Accountancy, Social Work, and Criminology.",
  },
  {
    year: "Today",
    title: "Growing Forward",
    body: "PEACI continues to grow with a continuously increasing student population and a committed faculty, carrying out its mission as a premier institution of learning in Muslim Mindanao.",
  },
];

const navLinks = [
  { href: "#history", label: "Brief History" },
  { href: "#mission", label: "Mission" },
  { href: "#vision", label: "Vision" },
  { href: "#objectives", label: "Objectives" },
  { href: "#goals", label: "Goals" },
];

const DEFAULT_MISSION =
  "Dedicated to introduce reforms to attain peace, prosperity, equality and sustainable development in the ARMM, the PEACI will lead the standards of excellence in science and technology, arts and sports; serve as the catalyst for the economic, cultural, socio-political, and agro-industrial development of the Muslims, Lumads and other indigenous groups, thereby transforming them into good productive members of the society possessing the highest degree of intelligence, honesty, morality, and excellence; and encourage them for the alleviation of poverty and the promotion of employment, and improved quality of life for all.";

const DEFAULT_VISION =
  "The Philippine Engineering and Agro-Industrial College, Inc. aspires to be the modern World-Class institution of highest academic excellence in the Autonomous Region in Muslim Mindanao (ARMM).";

const DEFAULT_OBJECTIVES = [
  "To provide competent, high moral, honest, trustworthy, service-oriented, intelligent, God-fearing and world-class graduates who are skillful and globally competitive in their special field of specialization.",
  "To produce the necessary workforce for the socio-economic, political and agro-industrial development of Mindanao, particularly the ARMM.",
  "To produce globally competitive technicians, educators, researchers and inventors who will serve as instruments for the attainment of mutual understanding, unity and lasting peace and sustainable development of the ARMM.",
];

const DEFAULT_GOALS = [
  "To provide and maintain a progressive educational institution of highest academic standards in strategic areas in Mindanao specially conflict-affected areas of the Autonomous Region in Muslim Mindanao (ARMM), where quality of education is in need.",
  "To offer elementary, secondary, tertiary, graduate and post-graduate courses as well as vocational, scientific and artistic courses in accordance with most updated, relevant, responsive and world-class curriculum at low and reasonable cost. It shall give emphasis on cultural development and preservation and scientific and Islamic researches necessary for the socio-economic, political, and religious development of ARMM.",
  "To provide free, available and accessible quality education for the poorest of the poor, orphans, under-privileged and disadvantaged children. It shall also give more attentive recognition, development and utilization of the extra-academically gifted.",
];

function About(props) {
  const [activeSection, setActiveSection] = useState("history");
  const [loading, setLoading] = useState(true);
  const [mission, setMission] = useState(DEFAULT_MISSION);
  const [vision, setVision] = useState(DEFAULT_VISION);
  const [objectives, setObjectives] = useState(DEFAULT_OBJECTIVES);
  const [goals, setGoals] = useState(DEFAULT_GOALS);

  useEffect(() => {
    document.title = "About PEACI";
    props.setActivePage();

    const fetchAbout = async () => {
      try {
        const snap = await getDoc(doc(db, "about", "content"));
        if (snap.exists()) {
          const data = snap.data();
          if (data.mission) setMission(data.mission);
          if (data.vision) setVision(data.vision);
          if (data.objectives?.length) setObjectives(data.objectives);
          if (data.goals?.length) setGoals(data.goals);
        }
      } catch (err) {
        console.error("Failed to load About content:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAbout();

    const sectionIds = navLinks.map(({ href }) => href.replace("#", ""));
    const observers = [];

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { threshold: 0.3 }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((obs) => obs.disconnect());
    // eslint-disable-next-line
  }, []);

  return (
    <div id="top" className="flex flex-col items-center w-screen z-10">

      {/* ── SIDEBAR NAV (desktop only) ── */}
      <SideNav activeSection={activeSection} />

      {/* ── HERO ── */}
      <div
        className="w-[100vw] flex flex-col items-center justify-center py-20 px-6 text-center bg-cover bg-no-repeat bg-top bg-blend-multiply bg-gradient-to-br from-[#eaac38] via-[#c48918] to-[#eaac38] dark:from-[#eaac38] dark:via-[#c48918] dark:to-[#eaac38]"
        style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${heroImg})` }}
      >
        <span className="inline-block bg-white/20 text-white text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
          Philippine Engineering &amp; Agro-Industrial College, Inc.
        </span>
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight tracking-tight drop-shadow-lg">
          About PEACI
        </h1>
        <p className="mt-6 max-w-2xl text-amber-100 text-lg md:text-xl font-light leading-relaxed">
          Committed to total development of honest, trustworthy and
          God-fearing servants in search of truth, virtue, justice, equality
          and academic excellence of the highest degree.
        </p>
        <a
          href="#history"
          className="mt-10 inline-flex items-center gap-2 bg-white text-amber-700 font-semibold text-sm px-6 py-3 rounded-full shadow-lg hover:bg-amber-50 transition-colors duration-200"
        >
          Explore
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4 animate-bounce"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </a>
      </div>

      {/* ── BRIEF HISTORY ── */}
      <section
        id="history"
        className="w-[100vw] flex flex-col items-center py-16 px-6 bg-white"
      >
        <div className="w-full max-w-5xl flex flex-col items-center">

          {/* Section label + heading */}
          <span className="text-amber-600 text-xs font-bold tracking-widest uppercase mb-3">
            Our Story
          </span>
          <h2 className="text-4xl font-extrabold text-gray-900 dark:text-gray-100 text-center mb-10">
            Brief History
          </h2>

          {/* ── A. STATS BAND ── */}
          <div className="flex flex-wrap justify-center gap-4 w-full mb-14">
            {stats.map(({ value, label }) => (
              <div
                key={label}
                className="flex flex-col items-center justify-center bg-amber-50 border border-amber-100 rounded-2xl py-7 px-8 shadow-sm w-44"
              >
                <span className="text-4xl font-extrabold text-amber-600 leading-none">
                  {value}
                </span>
                <span className="mt-2 text-sm font-medium text-slate-500 text-center">
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* ── B. SIDE-BY-SIDE INTRO ── */}
          <div className="flex flex-col md:flex-row gap-10 w-full items-center mb-16">
            <figure className="w-full md:w-1/2 flex-shrink-0">
              <div className="overflow-hidden rounded-2xl shadow-xl ring-1 ring-slate-200">
                <img
                  className="w-full h-auto object-cover"
                  src={cover}
                  alt="PEACI at CHED Anniversary 2016"
                />
              </div>
              <figcaption className="mt-3 text-sm text-center text-slate-500 italic">
                PEACI @ CHED Anniversary 2016
              </figcaption>
            </figure>
            <div className="w-full md:w-1/2 flex flex-col gap-4 text-slate-700 leading-relaxed text-base">
              <p>
                The Philippine Engineering and Agro-Industrial College, Inc.
                (PEACI) is a private, non-stock, non-profit educational
                institution, <strong>founded in 2007</strong> by a group of
                Meranao engineers. It is duly registered with the Securities and
                Exchange Commission and operates in accordance with the laws of
                the Republic of the Philippines.
              </p>
              <p>
                To ensure academic excellence, competent faculty members — many
                of them Cum Laude graduates from prestigious Philippine
                universities — were recruited from the very first year of
                operations. The teaching force has since tremendously grown
                across the institution's three departments.
              </p>
              <p>
                Initially offering BSCE, BSME, and BSEE alongside complete
                Primary, Secondary, and Kindergarten programs, PEACI has
                expanded to include BSIT, BEED, and other programs, all on a
                trimester curriculum designed for time-efficient student
                development.
              </p>
            </div>
          </div>

          {/* ── C. VERTICAL TIMELINE ── */}
          <div className="w-full mb-14">
            <h3 className="text-2xl font-bold text-slate-700 text-center mb-10">
              Milestones
            </h3>
            <div className="relative">
              {/* Center line — hidden on mobile, visible on md+ */}
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-amber-200 -translate-x-1/2" />

              <div className="flex flex-col gap-10">
                {timelineMilestones.map((item, i) => {
                  const isLeft = i % 2 === 0;
                  return (
                    <div
                      key={item.year}
                      className={`relative flex flex-col md:flex-row items-start md:items-center gap-4 ${
                        isLeft ? "md:flex-row" : "md:flex-row-reverse"
                      }`}
                    >
                      {/* Card */}
                      <div
                        className={`w-full md:w-[calc(50%-2rem)] bg-white rounded-2xl border border-gray-200 shadow-md p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 ${
                          isLeft ? "md:text-right" : "md:text-left"
                        }`}
                      >
                        <span className="inline-block bg-amber-600 text-white text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full mb-3">
                          {item.year}
                        </span>
                        <h4 className="text-lg font-bold text-slate-800 mb-2">
                          {item.title}
                        </h4>
                        <p className="text-slate-600 text-sm leading-relaxed">
                          {item.body}
                        </p>
                      </div>

                      {/* Center dot */}
                      <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-amber-600 ring-4 ring-white shadow z-10 items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-white" />
                      </div>

                      {/* Spacer for opposite side */}
                      <div className="hidden md:block w-[calc(50%-2rem)]" />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── D. MARAWI SIEGE CALLOUT ── */}
          <div className="w-full bg-amber-50 border-l-4 border-amber-500 rounded-2xl p-7 shadow-md mb-4 flex gap-5 items-start">
            <div className="flex-shrink-0 mt-0.5">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-7 h-7 text-amber-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 21V5a2 2 0 012-2h10l6 6v12a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14 3v6h6"
                />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 11v4M12 17h.01" />
              </svg>
            </div>
            <div>
              <p className="text-amber-700 text-xs font-bold tracking-widest uppercase mb-2">
                A Story of Resilience
              </p>
              <p className="text-slate-700 text-base leading-relaxed mb-2">
                In 2017, as one of the schools affected by the{" "}
                <strong>Marawi Siege</strong>, PEACI temporarily transferred
                its operations to its satellite school at 2nd East Street,
                Rosario Height, Tubod, Iligan City. Due to urgency and
                scarcity of resources, the institution later relocated to
                Brgy. Lomidong (Bo. Salam), Marawi City — which now serves as PEACI's main campus.
              </p>
              <p className="text-slate-600 text-sm italic">
                Despite adversities, PEACI has refused to cease, continuing to
                carry out its aspirations through courageously marking another
                beginning. Quality education for the nation's progress is what
                PEACI continues to uphold.
              </p>
            </div>
          </div>

          <BackToTop />
        </div>
      </section>

      {/* ── MISSION ── */}
      <section
        id="mission"
        className="w-[100vw] flex flex-col items-center py-16 px-6 bg-gradient-to-br from-[#eaac38] to-[#eaac38] dark:from-[#eaac38] dark:to-[#eaac38]"
      >
        <div className="w-full max-w-3xl flex flex-col items-center">
          <span className="text-amber-800 text-xs font-bold tracking-widest uppercase mb-3">
            Our Purpose
          </span>
          <h2 className="text-4xl font-extrabold text-white text-center mb-10">
            Mission
          </h2>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-10 shadow-2xl w-full">
            <QuoteIcon className="text-amber-400 mb-4" />
            {loading ? (
              <div className="h-6 bg-white/10 rounded animate-pulse w-full mb-3" />
            ) : (
              <p className="text-xl md:text-2xl italic font-light text-slate-200 leading-relaxed">
                {mission}
              </p>
            )}
          </div>
          <BackToTop />
        </div>
      </section>

      {/* ── VISION ── */}
      <section
        id="vision"
        className="w-[100vw] flex flex-col items-center py-16 px-6 bg-gradient-to-br from-[#eaac38] to-[#eaac38] dark:from-[#eaac38] dark:to-[#eaac38]"
      >
        <div className="w-full max-w-3xl flex flex-col items-center">
          <span className="text-amber-800 text-xs font-bold tracking-widest uppercase mb-3">
            Where We're Headed
          </span>
          <h2 className="text-4xl font-extrabold text-white text-center mb-10">
            Vision
          </h2>
          <div className="bg-white/10 border border-white/20 rounded-2xl p-8 md:p-10 shadow-2xl w-full backdrop-blur-sm">
            <QuoteIcon className="text-amber-400 mb-4" />
            {loading ? (
              <div className="h-6 bg-white/10 rounded animate-pulse w-full mb-3" />
            ) : (
              <p className="text-xl md:text-2xl italic font-light text-amber-50 leading-relaxed">
                {vision}
              </p>
            )}
          </div>
          <BackToTop />
        </div>
      </section>

      {/* ── OBJECTIVES ── */}
      <section
        id="objectives"
        className="w-[100vw] flex flex-col items-center py-16 px-6 bg-slate-50"
      >
        <div className="w-full max-w-4xl flex flex-col items-center">
          <span className="text-amber-600 text-xs font-bold tracking-widest uppercase mb-3">
            What We Aim For
          </span>
          <h2 className="text-4xl font-extrabold text-gray-900 dark:text-gray-100 text-center mb-10">
            Objectives
          </h2>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 w-full">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl border-l-4 border-sky-200 shadow-md p-6 h-28 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 w-full">
              {objectives.map((item, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl border-l-4 border-amber-500 shadow-md p-6 flex gap-4 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                >
                  <CheckIcon />
                  <p className="text-slate-700 text-base leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          )}
          <BackToTop />
        </div>
      </section>

      {/* ── GOALS ── */}
      <section
        id="goals"
        className="w-[100vw] flex flex-col items-center py-16 px-6 bg-white"
      >
        <div className="w-full max-w-4xl flex flex-col items-center">
          <span className="text-amber-600 text-xs font-bold tracking-widest uppercase mb-3">
            Our Commitment
          </span>
          <h2 className="text-4xl font-extrabold text-gray-900 dark:text-gray-100 text-center mb-10">
            Goals
          </h2>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 w-full">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-slate-50 rounded-2xl border-l-4 border-indigo-200 shadow-md p-6 h-28 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 w-full">
              {goals.map((item, i) => (
                <div
                  key={i}
                  className="bg-gray-50 rounded-2xl border-l-4 border-amber-500 shadow-md p-6 flex gap-4 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                >
                  <CheckIcon />
                  <p className="text-slate-700 text-base leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          )}
          <BackToTop />
        </div>
      </section>

    </div>
  );
}

export default About;
